// PDF text extraction using pdfjs-dist (runs in the webview — no Rust needed).
// Used by the Vacancy Evaluator as a cheaper alternative to screenshot-based
// extraction (3–5× fewer tokens, works with any LLM including Ollama).
import * as pdfjsLib from "pdfjs-dist";

// Point the worker at the bundled asset so Vite can resolve it.
// pdfjs requires a web worker; the esm-bundled build ships one.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const MAX_PAGES = 10;

export interface PdfExtractionResult {
  text: string;
  pageCount: number;
  wasScanned: boolean;   // true when no text layer found (image-only PDF)
  truncated: boolean;    // true when capped at MAX_PAGES
}

export async function extractPdfText(fileBytes: Uint8Array): Promise<PdfExtractionResult> {
  const loadingTask = pdfjsLib.getDocument({ data: fileBytes });
  const pdf = await loadingTask.promise;

  const totalPages = pdf.numPages;
  const pagesToRead = Math.min(totalPages, MAX_PAGES);
  const truncated = totalPages > MAX_PAGES;

  const pageTexts: string[] = [];

  for (let i = 1; i <= pagesToRead; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item: any) => ("str" in item ? item.str : ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    if (pageText) pageTexts.push(pageText);
  }

  const text = pageTexts.join("\n\n");
  const wasScanned = text.trim().length < 50;

  return { text, pageCount: totalPages, wasScanned, truncated };
}
